import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Box,
  Typography,
  Alert,
  IconButton,
  Divider,
  Autocomplete,
  Chip
} from '@mui/material';
import { Close, LocationOn, Label } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { Icon, DivIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to handle map clicks for location selection
const MapClickHandler: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });
  return null;
};

// Component to fly to location when it changes
const MapFlyTo: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();

  React.useEffect(() => {
    if (center && center[0] && center[1]) {
      map.flyTo(center, zoom, { duration: 1.5 });
    }
  }, [center[0], center[1], zoom, map]);

  return null;
};

interface AddStationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (stationData: any) => Promise<boolean>;
  initialLocation?: { lat: number; lng: number };
}

const AddStationDialog: React.FC<AddStationDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialLocation
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: initialLocation?.lat || '',
    longitude: initialLocation?.lng || '',
    elevation: '',
    owner: '',
    source: '',
    timezone: 'UTC',
    public: true,
    debug: false,
    notes: ''
  });
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [mapCenter, setMapCenter] = useState<[number, number]>([39.0742, 21.8243]); // Greece center (Thessaly)

  React.useEffect(() => {
    if (initialLocation) {
      setFormData(prev => ({
        ...prev,
        latitude: initialLocation.lat,
        longitude: initialLocation.lng
      }));
    }
  }, [initialLocation]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (error) setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Station name is required');
      return false;
    }
    if (!formData.latitude || !formData.longitude) {
      setError('Latitude and longitude are required');
      return false;
    }
    if (isNaN(Number(formData.latitude)) || isNaN(Number(formData.longitude))) {
      setError('Latitude and longitude must be valid numbers');
      return false;
    }
    if (Number(formData.latitude) < -90 || Number(formData.latitude) > 90) {
      setError('Latitude must be between -90 and 90');
      return false;
    }
    if (Number(formData.longitude) < -180 || Number(formData.longitude) > 180) {
      setError('Longitude must be between -180 and 180');
      return false;
    }
    if (formData.elevation && isNaN(Number(formData.elevation))) {
      setError('Elevation must be a valid number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const stationData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        location: {
          latitude: Number(formData.latitude),
          longitude: Number(formData.longitude)
        },
        public: formData.public,
        parser_config: formData.notes.trim() ? { notes: formData.notes.trim() } : undefined,
        tags: tags.length > 0 ? tags : undefined
      };

      const success = await onSubmit(stationData);
      if (success) {
        handleClose();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create station');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      latitude: '',
      longitude: '',
      elevation: '',
      owner: '',
      source: '',
      timezone: 'UTC',
      public: true,
      debug: false,
      notes: ''
    });
    setTags([]);
    setError('');
    onClose();
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({
          ...prev,
          latitude: latitude,
          longitude: longitude
        }));
        setMapCenter([latitude, longitude]);
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMsg = 'Failed to get current location. ';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg += 'Please enable location permissions in your browser settings. Note: Geolocation only works on HTTPS sites in production.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg += 'Location information is unavailable. This may happen if GPS is disabled or you are in an area with poor signal. Try entering coordinates manually.';
            break;
          case error.TIMEOUT:
            errorMsg += 'Location request timed out. Try again or enter coordinates manually.';
            break;
          default:
            errorMsg += 'An unknown error occurred. Try entering coordinates manually.';
        }

        setError(errorMsg);
      },
      {
        enableHighAccuracy: false,  // Set to false for better compatibility
        timeout: 15000,  // Increased timeout
        maximumAge: 60000  // Allow cached position up to 1 minute old
      }
    );
  };

  const handleMapLocationSelect = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
  };

  const timezones = [
    'UTC',
    'Europe/Athens',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney'
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">
            Add New Station
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Station Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
                placeholder="e.g., Central Park Weather Station"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Brief description of the station and its purpose"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <LocationOn sx={{ mr: 1 }} />
                Location
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="latitude"
                label="Latitude"
                type="number"
                value={formData.latitude}
                onChange={handleInputChange}
                fullWidth
                required
                inputProps={{ step: 'any', min: -90, max: 90 }}
                placeholder="40.7128"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="longitude"
                label="Longitude"
                type="number"
                value={formData.longitude}
                onChange={handleInputChange}
                fullWidth
                required
                inputProps={{ step: 'any', min: -180, max: 180 }}
                placeholder="-74.0060"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                onClick={getCurrentLocation}
                variant="outlined"
                size="small"
                startIcon={<LocationOn />}
              >
                Use Current Location
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Click on the map to select location
              </Typography>
              <Box sx={{ height: '300px', borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}>
                <MapContainer
                  center={mapCenter}
                  zoom={6}
                  style={{ height: '100%', width: '100%' }}
                  attributionControl={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | &copy; <a href="https://cartodb.com/">CartoDB</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    subdomains="abcd"
                    maxZoom={19}
                  />
                  <MapClickHandler onLocationSelect={handleMapLocationSelect} />
                  {formData.latitude && formData.longitude && (
                    <>
                      <MapFlyTo
                        center={[Number(formData.latitude), Number(formData.longitude)]}
                        zoom={13}
                      />
                      <Marker
                        position={[Number(formData.latitude), Number(formData.longitude)]}
                      />
                    </>
                  )}
                </MapContainer>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="elevation"
                label="Elevation (meters)"
                type="number"
                value={formData.elevation}
                onChange={handleInputChange}
                fullWidth
                placeholder="15.5"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="timezone"
                label="Timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                fullWidth
                select
              >
                {timezones.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="owner"
                label="Owner"
                value={formData.owner}
                onChange={handleInputChange}
                fullWidth
                placeholder="Organization or individual name"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                name="source"
                label="Source"
                value={formData.source}
                onChange={handleInputChange}
                fullWidth
                select
              >
                <MenuItem value="">
                  <em>Select source type</em>
                </MenuItem>
                <MenuItem value="HTTP">HTTP</MenuItem>
                <MenuItem value="MQTT">MQTT</MenuItem>
                <MenuItem value="CoAP">CoAP</MenuItem>
                <MenuItem value="LoRaWAN">LoRaWAN</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                value={formData.notes}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Additional notes or comments"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={tags}
                onChange={(event, newValue) => {
                  setTags(newValue as string[]);
                }}
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Type and press Enter to add tags (e.g., Home, Work, Outdoor)"
                    helperText="Add tags to group and filter stations"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="public"
                      checked={formData.public}
                      onChange={handleInputChange}
                      color="primary"
                    />
                  }
                  label="Public Station"
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="debug"
                      checked={formData.debug}
                      onChange={handleInputChange}
                      color="primary"
                    />
                  }
                  label="Debug Mode"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Station'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddStationDialog;